import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
  User, 
  Mail, 
  Phone, 
  FileText, 
  CheckCircle, 
  XCircle, 
  Clock, 
  AlertTriangle,
  Download,
  Calendar,
  DollarSign,
  Package,
  Eye,
  Trash2,
  Plus,
  X
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DriverProfileDialog = ({ open, onOpenChange, driver }) => {
  const { t } = useTranslation();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({
    totalServices: 0,
    completedServices: 0,
    totalEarnings: 0
  });
  const [previewDocument, setPreviewDocument] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(null);
  const [showUploadDialog, setShowUploadDialog] = useState(false);

  useEffect(() => {
    if (open && driver) {
      loadDriverData();
    }
  }, [open, driver]);

  const loadDriverData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      
      // Load documents
      const docsResponse = await fetch(`${BACKEND_URL}/api/drivers/${driver.id}/documents`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (docsResponse.ok) {
        const docsData = await docsResponse.json();
        setDocuments(docsData || []);
      }

      // Load services stats (you can extend this)
      // const statsResponse = await fetch(`${BACKEND_URL}/api/admin/drivers/${driver.id}/stats`, {
      //   headers: { 'Authorization': `Bearer ${token}` }
      // });
      
    } catch (error) {
      console.error('Error loading driver data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getDocumentStatusBadge = (doc) => {
    if (!doc) return { text: t('driver_doc_not_uploaded'), color: 'bg-gray-100 text-gray-600', icon: XCircle };
    
    const now = new Date();
    const expiryDate = doc.expiry_date ? new Date(doc.expiry_date) : null;
    const daysToExpiry = expiryDate ? Math.floor((expiryDate - now) / (1000 * 60 * 60 * 24)) : null;
    
    if (doc.status === 'approved') {
      if (daysToExpiry !== null && daysToExpiry < 30) {
        return { text: t('driver_doc_expires_soon'), color: 'bg-orange-100 text-orange-800', icon: AlertTriangle };
      }
      return { text: t('driver_doc_approved'), color: 'bg-green-100 text-green-800', icon: CheckCircle };
    }
    if (doc.status === 'pending') return { text: t('driver_doc_pending'), color: 'bg-yellow-100 text-yellow-800', icon: Clock };
    if (doc.status === 'rejected') return { text: t('driver_doc_rejected'), color: 'bg-red-100 text-red-800', icon: XCircle };
    if (doc.status === 'expired') return { text: t('driver_doc_expired'), color: 'bg-red-100 text-red-800', icon: AlertTriangle };
    
    return { text: doc.status, color: 'bg-gray-100 text-gray-600', icon: FileText };
  };

  const getDocumentName = (docType) => {
    const keyMap = {
      'dni_front': 'driver_doc_dni_front',
      'dni_back': 'driver_doc_dni_back',
      'license_front': 'driver_doc_license_front',
      'license_back': 'driver_doc_license_back',
      'autonomo_certificate': 'driver_doc_autonomo',
      'hacienda_certificate': 'driver_doc_hacienda',
      'ss_certificate': 'driver_doc_ss'
    };
    return keyMap[docType] ? t(keyMap[docType]) : docType;
  };

  const handlePreview = async (doc) => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${BACKEND_URL}/api/documents/${doc.id}/download`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        window.open(url, '_blank');
      } else {
        // Try to get error message from response
        let errorMessage = t('driver_error_open_document');
        try {
          const errorData = await response.json();
          if (errorData.detail) {
            errorMessage = errorData.detail;
          }
        } catch (e) {
          // Ignore JSON parse error
        }
        alert(errorMessage);
      }
    } catch (error) {
      console.error('Error previewing document:', error);
      alert(t('driver_doc_alert_connection_error'));
    }
  };

  const handleDownload = async (doc) => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${BACKEND_URL}/api/documents/${doc.id}/download`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${getDocumentName(doc.document_type)}_${driver.name}${doc.file_path?.includes('.pdf') ? '.pdf' : '.jpg'}`;
        document.body.appendChild(link);
        link.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(link);
      } else {
        alert(t('driver_doc_alert_download_error'));
      }
    } catch (error) {
      console.error('Error downloading document:', error);
      alert(t('driver_doc_alert_download_error'));
    }
  };

  const handleDelete = async (docId) => {
    if (!window.confirm('¿Estás seguro de que quieres eliminar este documento?')) return;
    
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${BACKEND_URL}/api/drivers/documents/${docId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        alert(t('driver_doc_alert_deleted'));
        loadDriverData(); // Reload documents
      } else {
        alert(t('driver_doc_alert_error_delete'));
      }
    } catch (error) {
      console.error('Error deleting document:', error);
      alert(t('driver_doc_alert_error_delete'));
    }
  };

  const handleUploadNew = (docType) => {
    setUploadingDoc(docType);
    setShowUploadDialog(true);
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);
    formData.append('document_type', uploadingDoc);
    formData.append('driver_id', driver.id);
    formData.append('upload_date', new Date().toISOString()); // Full ISO string with timestamp
    
    // Add expiry date for documents that need it (1 year from now)
    const expiryDate = new Date();
    expiryDate.setFullYear(expiryDate.getFullYear() + 1);
    formData.append('expiry_date', expiryDate.toISOString()); // Full ISO string with timestamp

    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${BACKEND_URL}/api/drivers/documents/upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      if (response.ok) {
        alert(t('driver_doc_alert_uploaded'));
        loadDriverData(); // Reload documents
        setShowUploadDialog(false);
        setUploadingDoc(null);
      } else {
        const error = await response.json();
        alert(t('driver_doc_alert_error_upload_detail', { detail: error.detail || 'Error desconocido' }));
      }
    } catch (error) {
      console.error('Error uploading document:', error);
      alert(t('driver_doc_alert_error_upload'));
    }
  };

  if (!driver) return null;

  const requiredDocs = driver.fleet_driver_id 
    ? ['dni_front', 'dni_back', 'license_front', 'license_back']
    : ['dni_front', 'dni_back', 'license_front', 'license_back', 'autonomo_certificate', 'hacienda_certificate', 'ss_certificate'];

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl">{t('driver_profile_title')}</DialogTitle>
          <DialogDescription>
            {t('driver_profile_subtitle')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <User className="w-5 h-5" />
                {t('driver_basic_info')}
              </CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600">{t('driver_name_label')}</p>
                <p className="font-semibold">{driver.name}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Email</p>
                <p className="font-semibold flex items-center gap-1">
                  <Mail className="w-4 h-4" />
                  {driver.email}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600">{t('driver_phone_label')}</p>
                <p className="font-semibold flex items-center gap-1">
                  <Phone className="w-4 h-4" />
                  {driver.phone || t('driver_not_specified')}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600">DNI</p>
                <p className="font-semibold">{driver.dni_nie || t('driver_not_specified')}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">{t('driver_type_label')}</p>
                <p className="font-semibold">
                  {driver.fleet_driver_id ? `👥 ${t('driver_fleet_member')}` : `⭐ ${t('driver_independent')}`}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600">{t('driver_doc_status')}</p>
                <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-sm font-medium ${
                  driver.documents_validated 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-yellow-100 text-yellow-800'
                }`}>
                  {driver.documents_validated ? `✓ ${t('driver_validated')}` : `⏳ ${t('driver_doc_pending')}`}
                </span>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <FileText className="w-5 h-5" />
                {t('driver_documents')} ({documents.length} {t('driver_subidos_required', { required: requiredDocs.length })})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <p className="text-gray-500 text-center py-4">{t('driver_docs_loading')}</p>
              ) : (
                <div className="space-y-2">
                  {requiredDocs.map((docType) => {
                    const doc = documents.find(d => d.document_type === docType);
                    const status = getDocumentStatusBadge(doc);
                    const StatusIcon = status.icon;
                    const now = new Date();
                    const expiryDate = doc?.expiry_date ? new Date(doc.expiry_date) : null;
                    const daysToExpiry = expiryDate ? Math.floor((expiryDate - now) / (1000 * 60 * 60 * 24)) : null;
                    const isExpiringSoon = daysToExpiry !== null && daysToExpiry < 30;
                    
                    return (
                      <div key={docType} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100">
                        <div className="flex items-center gap-3 flex-1">
                          <FileText className="w-5 h-5 text-gray-400" />
                          <div className="flex-1">
                            <p className="font-medium">{getDocumentName(docType)}</p>
                            {doc && doc.expiry_date && (
                              <p className={`text-xs flex items-center gap-1 ${isExpiringSoon ? 'text-orange-600 font-semibold' : 'text-gray-500'}`}>
                                <Calendar className="w-3 h-3" />
                                {t('driver_expires')} {new Date(doc.expiry_date).toLocaleDateString()}
                                {isExpiringSoon && ' ⚠️'}
                              </p>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <span className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${status.color}`}>
                              <StatusIcon className="w-3 h-3" />
                              {status.text}
                            </span>
                            {doc && doc.file_url ? (
                              <>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handlePreview(doc)}
                                  title={t('driver_view_doc_tooltip')}
                                  className="h-8 text-blue-600 border-blue-600 hover:bg-blue-50"
                                >
                                  <Eye className="w-4 h-4 mr-1" />
                                  {t('driver_view')}
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleDownload(doc)}
                                  title={t('driver_download_doc_tooltip')}
                                  className="h-8 text-green-600 border-green-600 hover:bg-green-50"
                                >
                                  <Download className="w-4 h-4 mr-1" />
                                  {t('driver_download')}
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleDelete(doc.id)}
                                  title={t('driver_delete_doc_tooltip')}
                                  className="h-8 text-red-600 hover:text-red-800 hover:bg-red-50"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </>
                            ) : (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleUploadNew(docType)}
                                className="h-8 text-green-600 border-green-600 hover:bg-green-50"
                              >
                                <Plus className="w-4 h-4 mr-1" />
                                {t('driver_upload_new')}
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Stats */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Package className="w-5 h-5" />
                {t('driver_statistics')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center p-4 bg-blue-50 rounded-lg">
                  <p className="text-2xl font-bold text-blue-600">{stats.totalServices}</p>
                  <p className="text-sm text-gray-600">{t('driver_total_services')}</p>
                </div>
                <div className="text-center p-4 bg-green-50 rounded-lg">
                  <p className="text-2xl font-bold text-green-600">{stats.completedServices}</p>
                  <p className="text-sm text-gray-600">{t('driver_completed')}</p>
                </div>
                <div className="text-center p-4 bg-purple-50 rounded-lg">
                  <DollarSign className="w-6 h-6 mx-auto text-purple-600 mb-1" />
                  <p className="text-2xl font-bold text-purple-600">{stats.totalEarnings.toFixed(2)}€</p>
                  <p className="text-sm text-gray-600">{t('driver_earnings')}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {(driver.bank_account || driver.tax_id) && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <DollarSign className="w-5 h-5" />
                  {t('driver_billing_info')}
                </CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {driver.bank_account && (
                  <div>
                    <p className="text-sm text-gray-600">{t('driver_bank_account')}</p>
                    <p className="font-mono text-sm">{driver.bank_account}</p>
                  </div>
                )}
                {driver.tax_id && (
                  <div>
                    <p className="text-sm text-gray-600">{t('driver_tax_id')}</p>
                    <p className="font-semibold">{driver.tax_id}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </DialogContent>
    </Dialog>

    {/* Preview Dialog */}
    <Dialog open={showPreview} onOpenChange={setShowPreview}>
      <DialogContent className="max-w-4xl max-h-[90vh]">
        <DialogHeader>
          <DialogTitle>{t('driver_preview_title')} - {previewDocument && getDocumentName(previewDocument.document_type)}</DialogTitle>
          <button 
            onClick={() => setShowPreview(false)}
            className="absolute right-4 top-4 rounded-sm opacity-70 hover:opacity-100"
          >
            <X className="h-4 w-4" />
          </button>
        </DialogHeader>
        {previewDocument && (
          <div className="w-full h-[70vh] overflow-auto">
            <iframe
              src={`${BACKEND_URL}${previewDocument.file_url}`}
              className="w-full h-full border-0"
              title={t('common_document_preview')}
            />
          </div>
        )}
      </DialogContent>
    </Dialog>

    {/* Upload Dialog */}
    <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('driver_upload_new_document')}</DialogTitle>
          <DialogDescription>
            {uploadingDoc && getDocumentName(uploadingDoc)}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
            <input
              type="file"
              accept=".pdf,.jpg,.jpeg,.png"
              onChange={handleFileUpload}
              className="hidden"
              id="document-upload"
            />
            <label 
              htmlFor="document-upload"
              className="cursor-pointer flex flex-col items-center gap-2"
            >
              <Plus className="w-12 h-12 text-gray-400" />
              <p className="text-sm text-gray-600">
                Haz clic para seleccionar un archivo
              </p>
              <p className="text-xs text-gray-400">
                PDF, JPG o PNG (máx. 10MB)
              </p>
            </label>
          </div>
          <p className="text-xs text-gray-500">
            Este documento reemplazará el anterior y quedará pendiente de aprobación.
          </p>
        </div>
      </DialogContent>
    </Dialog>
    </>
  );
};

export default DriverProfileDialog;
